import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Sidebar } from 'primeng/sidebar';
import { StorageService } from '../../services/storage.service';
import { Router } from '@angular/router';
import { AuthUser } from '../../shared/interface';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { PASSWORD_HINT, passwordMatchValidator, strongPasswordValidator } from '../../shared/password.validators';

@Component({
  selector: 'app-struttura-backoffice',
  templateUrl: './struttura-backoffice.component.html',
  styleUrl: './struttura-backoffice.component.css'
})
export class StrutturaBackofficeComponent implements OnInit, OnDestroy {

  constructor(private apiService: ApiService, private storageService: StorageService, private router: Router) { }

  sidebar = false;
  password = false;
  forcePasswordChange = false;
  totpDialog = false;
  totpEnabled = false;
  totpSetup: { secret: string; qr: string; otpauthUrl: string } | null = null;
  passwordHint = PASSWORD_HINT;

  user?: AuthUser | null;

  formPassword!: FormGroup;
  formTotpEnable!: FormGroup;
  formTotpDisable!: FormGroup;

  @ViewChild('sidebarRef') sidebarRef!: Sidebar;

  ngOnInit(): void {
    document.documentElement.classList.add('bo-scroll-lock');
    this.user = this.storageService.getUser();

    // Flag anche dal JWT (fonte di verità server)
    try {
      const token = this.storageService.getToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload?.mustChangePassword) {
          this.storageService.patchUser({ mustChangePassword: true });
          this.user = this.storageService.getUser();
        }
      }
    } catch { /* ignore */ }

    this.formPassword = new FormGroup({
      current_password: new FormControl(null, Validators.required),
      password: new FormControl(null, [Validators.required, strongPasswordValidator()]),
      confirm_password: new FormControl(null, [Validators.required]),
    }, { validators: passwordMatchValidator() });

    this.formTotpEnable = new FormGroup({
      code: new FormControl(null, [Validators.required, Validators.pattern(/^\d{6}$/)]),
    });
    this.formTotpDisable = new FormGroup({
      password: new FormControl(null, Validators.required),
      code: new FormControl(null, [Validators.required, Validators.pattern(/^\d{6}$/)]),
    });

    if (this.user?.mustChangePassword) {
      this.forcePasswordChange = true;
      this.password = true;
    } else {
      this.refreshTotpStatus();
    }
  }

  ngOnDestroy(): void {
    document.documentElement.classList.remove('bo-scroll-lock');
  }

  refreshTotpStatus() {
    this.apiService.getTotpStatus().subscribe({
      next: (s) => this.totpEnabled = !!s.totp_enabled,
      error: () => { /* ignore if offline */ },
    });
  }

  closeCallback(e: any): void {
    this.sidebarRef.close(e);
  }

  onLogOut() {
    this.storageService.clearStorage();
    this.router.navigate(['/backoffice/login']);
  }

  onPassword() {
    if (!this.formPassword.valid || !this.user?.id) return;
    const wasForced = this.forcePasswordChange;
    this.apiService.updatePasswordUtente({
      password: this.formPassword.get('password')?.value,
      current_password: this.formPassword.get('current_password')?.value,
    }, this.user.id).subscribe({
      next: (res: any) => {
        if (res?.accessToken) this.storageService.setToken(res.accessToken);
        this.storageService.patchUser({ mustChangePassword: false });
        this.user = this.storageService.getUser();
        this.forcePasswordChange = false;
        this.password = false;
        this.formPassword.reset();
        this.apiService.message('success', 'Modifica', wasForced
          ? 'Password aggiornata. Ora puoi usare il backoffice.'
          : 'Password aggiornata');
        this.refreshTotpStatus();
      },
      error: (err) => this.apiService.message('error', 'Errore', err.error?.message?.message || 'Errore'),
    });
  }

  onPasswordDialogVisible(visible: boolean) {
    if (!visible && this.forcePasswordChange) {
      this.password = true;
      return;
    }
    this.password = visible;
  }

  openTotp() {
    this.totpDialog = true;
    this.totpSetup = null;
    this.formTotpEnable.reset();
    this.formTotpDisable.reset();
    this.refreshTotpStatus();
  }

  startTotpSetup() {
    this.apiService.setupTotp().subscribe({
      next: (res) => this.totpSetup = res,
      error: (err) => this.apiService.message('error', 'Errore', err.error?.message?.message || 'Errore setup 2FA'),
    });
  }

  confirmTotpEnable() {
    if (!this.formTotpEnable.valid) return;
    this.apiService.enableTotp(String(this.formTotpEnable.get('code')?.value)).subscribe({
      next: () => {
        this.totpEnabled = true;
        this.totpSetup = null;
        this.totpDialog = false;
        this.apiService.message('success', '2FA', 'Autenticazione a due fattori attivata');
      },
      error: (err) => this.apiService.message('error', 'Errore', err.error?.message?.message || 'Codice non valido'),
    });
  }

  confirmTotpDisable() {
    if (!this.formTotpDisable.valid) return;
    this.apiService.disableTotp(
      this.formTotpDisable.get('password')?.value,
      String(this.formTotpDisable.get('code')?.value)
    ).subscribe({
      next: () => {
        this.totpEnabled = false;
        this.totpDialog = false;
        this.apiService.message('success', '2FA', 'Autenticazione a due fattori disattivata');
      },
      error: (err) => this.apiService.message('error', 'Errore', err.error?.message?.message || 'Errore'),
    });
  }

}
